import './style.scss'
import { register } from '@polleverywhere/hmr-custom-element'
import { html, render } from 'lit-html'
import { Chart } from '@polleverywhere/viz-chart'
import { Dimensions, Settings } from '@polleverywhere/viz-models'
import { debounce } from 'underscore'
import { classic } from '@polleverywhere/viz-themes'
import Backbone from '@polleverywhere/backbone'
import env from '@polleverywhere/core/lib/env'
import { initializeTypekit, applyFont } from '@polleverywhere/viz-utils/lib/load-custom-font'
import '@polleverywhere/icon-element'
import { defaultLocale } from '@polleverywhere/viz-utils/lib/load-locale'
import '@polleverywhere/viz-brandbar'
import { bemBlock } from '@polleverywhere/bem-helpers/src/index'
import { getScale } from '@polleverywhere/viz-utils'

const DEFAULT_LOCALE = defaultLocale()
const BEM = bemBlock('activity-visualization')

function clean () {
  this.chart && this.chart.remove()
  this.brandbar && this.brandbar.remove()
  this.children[0] && this.children[0].remove()
  this.chart = null
  this.brandbar = null
}

function skeletonTemplate () {
  return html`
    <div class='activity-visualization__skeleton'>
      <div class='element-skeleton-progress'></div>
      <div class='element-skeleton-progress'></div>
      <div class='element-skeleton-progress'></div>
      <div class='element-skeleton-progress'></div>
      <div class='element-skeleton-progress'></div>
    </div>
  `
}

function fourOhFourTemlate () {
  return html`
    <div class='activity-visualization__not-found'>
      <icon-element type='legacy-svg' name='pe-logo-icon-black'></icon-element>
      <h1>Activity not found</h1>
      <p>Make sure the URL is correct or see if the activity was deleted</p>
    </div>
  `
}

function brandingTemplate (flashOptionsTheme, hideUpsells) {
  return html`<viz-brandbar
      theme=${flashOptionsTheme}
      ?hide-upsells=${hideUpsells}
      @upsell-clicked=${() => window.open(upsellUrl())}}
  ></viz-brandbar>`
}

function toggleFontScalerEnabled (enable) {
  if (enable) {
    if (!this.fontScaler) {
      this.fontScaler = debounce(updateFontScale.bind(this), 50)
    }

    window.addEventListener('resize', this.fontScaler)
  } else {
    this.fontScaler && window.removeEventListener('resize', this.fontScaler)
    this.fontScaler = null
  }
}

function toggleResizerEnabled (enable) {
  if (enable) {
    if (!this.resizer) {
      this.resizer = debounce(updateAspectRatio.bind(this), 50)
    }

    window.addEventListener('resize', this.resizer)
  } else {
    this.resizer && window.removeEventListener('resize', this.resizer)
    this.resizer = null
  }
}

function setupFlashoptions (flashoptionsModel) {
  if (!flashoptionsModel) return

  if (flashoptionsModel.attributes.length < 2) {
    flashoptionsModel.set(classic)
  }

  if (!flashoptionsModel.has('font')) {
    flashoptionsModel.set('font', 'source-sans-pro')
  }
}

function builDimensionsModel (element, activity) {
  const { width, height } = element.getBoundingClientRect()
  const dimensions = new Dimensions(null, {
    model: activity,
    $el: Backbone.$(element),
    width,
    height
  })

  dimensions.width = () => element.getBoundingClientRect().width
  dimensions.height = () => element.getBoundingClientRect().height
  dimensions.update()

  return dimensions
}

function updateFooterTheme (e) {
  const vizFooter = this.el.querySelector('viz-footer')
  if (vizFooter) {
    vizFooter.theme = this.model.flashOptions().get('themeId')
  }
}

async function renderTemplate () {
  if (!this.isConnected) return

  updateAspectRatio.call(this)
  clean.call(this)

  if (!this.model) {
    render(fourOhFourTemlate(), this)
    this.dispatchEvent('render')
    return
  }

  if (!this.model.id || this.model.id < 0) {
    render(skeletonTemplate(), this)
    this.dispatchEvent('render')
    return
  }

  if (typeof this.model.flashOptions === 'function') {
    setupFlashoptions(this.model.flashOptions())
  }

  const dimensions = builDimensionsModel(this, this.model)
  this.settings.set('animation', animationEnabled.call(this))

  this.chart = new Chart({
    i18n: this.i18n,
    model: this.model,
    settings: this.settings,
    app: this.app,
    dimensions,
    user: this.user,
    showVotingInstructions: votingInstructionsEnabled.call(this),
    autoScale: false
  })

  this.chart.listenTo(this.chart, 'login', () => this.dispatchEvent('login'))
  this.chart.listenTo(this.chart, 'qrcode', () => this.dispatchEvent('qrcode'))
  this.chart.listenTo(this.chart, 'resize', () => this.dispatchEvent('resize'))
  this.chart.listenTo(this.chart, 'render', () => requestAnimationFrame(() => {
    this.dispatchEvent('render')
  }))
  this.chart.listenTo(this.model.flashOptions(), 'change:themeId', updateFooterTheme)
  this.chart.listenTo(this.model.flashOptions(), 'change:font', async (_, font) => {
    try {
      await applyFont(this.chart.el, font)
    } catch (error) { }

    this.dispatchEvent('resize')
  })

  try {
    await fontsLoaded.call(this)
  } catch (error) { }

  // If we are loading an invalid font, this async function will fail.
  // However, when it fails the fallback fonts will be applied correctly,
  // and everything will still function correctly.
  try {
    await applyFont(this, this.model.flashOptions().get('font'))
  } catch (error) { }

  // NOTE: There could be an edge case where renderTemplate is still executing
  // since it's an async function, disconnectedCallback could also be called at
  // the same time. This would result in this.chart to be null.
  if (!this.chart) return

  this.chart.render()

  const el = document.createElement('div')
  el.classList.add(BEM('wrapper'))
  this.appendChild(el)
  setFontScale.call(this)

  const showBranding = this.model.get('show_branding')
  const hideUpsells = (this.model.get('hide') || []).includes('upsells')
  const flashOptionsTheme = this.model.flashOptions().get('themeId') || 'classic'

  render(html`
    ${this.chart.el}
    <footer class="${BEM('wrapper-footer')}">
      ${showBranding ? brandingTemplate(flashOptionsTheme, hideUpsells) : ''}
    </footer>
  `, el)
}

function upsellUrl () {
  return env.get('api_url') + '/plans'
}

function aspectRatio () {
  if (this.hasAttribute('aspect-ratio')) {
    return parseFloat(this.getAttribute('aspect-ratio')) || 16 / 9
  }
}

function votingInstructionsEnabled () {
  if (this.hasAttribute('voting-instructions')) {
    return !!JSON.parse(this.getAttribute('voting-instructions'))
  }
}

function animationEnabled () {
  if (!this.hasAttribute('animation')) {
    return this.settings.has('animation')
      ? this.settings.get('animation')
      : true
  }

  return !!JSON.parse(this.getAttribute('animation'))
}

function setFontScale () {
  if (this.chart && this.children[0]) {
    this.children[0].style.fontSize = `${getScale(this.chart.dimensions.width(), this.chart.dimensions.height()) * 100}%`
  }
}

function updateAspectRatio () {
  const ratio = aspectRatio.call(this)

  if (ratio) {
    const rect = this.getBoundingClientRect()
    const width = rect.width
    const targetHeight = (width / ratio)

    this.style.height = `${targetHeight}px`
  }
}

function updateFontScale () {
  if (this.chart) {
    this.chart.dimensions.update()
    setFontScale.call(this)
  }
}

function fontsLoaded () {
  return new Promise((resolve) => {
    if (this.getAttribute('load-fonts') === null) return resolve()
    initializeTypekit(env.get('typekit_id')).then(resolve, resolve)
  })
}

/**
 * Renders a live chart for an activity
 *
 * @element activity-visualization
 */
export class ActivityVisualization extends HTMLElement {
  static get tagName () { return 'activity-visualization' }

  /**
   * @attr {number} [aspect-ratio=16/9] - Aspect ratio to render chart
   * @attr {boolean} [animation=true] - Toggle animation
   * @attr {booolean} [voting-instructions='true'] - Toggle voting instructions
   */
  static get observedAttributes () {
    return [
      'aspect-ratio',
      'animation',
      'voting-instructions',
      'load-fonts'
    ]
  }

  get loadFonts () {
    return this.getAttribute('load-fonts') || false
  }

  /**
   * Prevent re-setting the load-fonts attribute.
   */
  set loadFonts (enable) {
    if (this.getAttribute('load-fonts') !== null) return

    this.setAttribute('load-fonts', enable)

    if (!enable) return
    if (!env.has('typekit_id')) return
    if (!this.chart) { return }

    fontsLoaded.call(this).finally(() => {
      applyFont(this.chart.el, this.model.flashOptions().get('font'))
        .catch(() => { })
    })
  }

  constructor () {
    super()
    this.app = this.app ?? new Backbone.Model()
    this.settings = this.settings ?? new Settings()
    this.app.settings = this.app.settings ?? this.settings

    if (!env.has('aftermath_url')) env.set('aftermath_url', 'https://aftermath.polleverywhere.com')

    toggleResizerEnabled.call(this, !!aspectRatio.call(this))
    toggleFontScalerEnabled.call(this, true)
  }

  /**
   * @prop {Model} model - Activity model
   */
  get model () {
    return this._model
  }

  set model (model) {
    if (this.model) this.model.off('change:id')

    this._model = model

    if (this._model) {
      this._model.on('change:id', renderTemplate.bind(this))
    }

    renderTemplate.call(this)
  }

  get i18n () {
    return this._i18n || DEFAULT_LOCALE
  }

  set i18n (i18n) {
    this._i18n = i18n
    renderTemplate.call(this)
  }

  get user () {
    return this._user
  }

  set user (user) {
    this._user = user
  }

  get settings () {
    return this._settings
  }

  set settings (settings) {
    this._settings = settings
  }

  get app () {
    return this._app
  }

  set app (app) {
    this._app = app
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  disconnectedCallback () {
    if (this.model) this.model.off('change:id')
    clean.call(this)
    toggleResizerEnabled.call(this, false)
    toggleFontScalerEnabled.call(this, false)
  }

  attributeChangedCallback (name, _oldvalue, newvalue) {
    // changing animation attribute needs to rerender the chart
    if (name === 'animation') {
      renderTemplate.call(this)
      return
    }

    if (name === 'load-fonts') {
      this.loadFonts = newvalue
      return
    }

    if (name === 'aspect-ratio') {
      toggleResizerEnabled.call(this, !!newvalue)
    }

    updateAspectRatio.call(this)
    updateFontScale.call(this)
  }

  dispatchEvent (channel = '', details = {}, bubbles = true) {
    return super.dispatchEvent(new CustomEvent(channel, {
      bubbles,
      details
    }))
  }
}

register(module, ActivityVisualization.tagName, ActivityVisualization)
